import { useCallback, useMemo, useEffect, memo } from 'react';
import loadable from '@loadable/component';
import SEO from '../SEO';
import EndState from '../EndState';
import Header from '../Header';
import { OneColumn } from '@/components/Layout';
import { Grid, useTheme, Bones } from '@simplywallst/ui-core';
import { MarketIndustryFilter } from '@/components/Header';
import useMarketOptions from '@/pages/Stocks/hooks/useMarketOptions';
import { useIndustryOptionsByGridRule } from '@/hooks/useIndustryOptionsByGridRule';
import { useReduxStateForDiscover } from '../../hooks/useReduxStateForDiscover';
import useMetaData from '../../hooks/useMetaData';
import { ROUTE_INVESTING_IDEA_DISCOVERY_PAGE } from '@/constants/routes';
import type { PrimaryCountryISOElseGlobal } from '@/constants/countries';
import { getPageType, getStatementKeys } from '../../utils';
import { updateRule } from '@/redux/grid/utils';
import { Wrapper, Filter, TotalRecords, Message } from './styled';
import { pluralise } from '@utilities/pluralise';
import { MiniPriceChartDefinitions } from '@/components/MiniPriceChart';
import If from '@/components/If';
import useUser from '@/hooks/useUser';
import track, { useTracking } from 'react-tracking';
import Media from 'react-media';
import { useUserAgent } from '@simplywallst/hooks/useUserAgent';
import { RouterLink } from '@redux-first-router-link';
import type { PageType, PageId } from '@/pages/DiscoverPageV2/constants';
import { useGridCompanies } from '@/hooks/useGridCompanies';
import { useGridView } from '@/hooks/useGridView';
import type { Company } from '@/hooks/useGridCompanies';

import HorizontallySlidingCompanyTile from '@/components/HorizontallySlidingCompanyTile';

const StatementsSlide = loadable(
  () => import('@/components/HorizontallySlidingCompanyTile/StatementsSlide')
);

const RisksRewardsSlide = loadable(
  () => import('@/components/HorizontallySlidingCompanyTile/RisksRewardsSlide')
);

const AnalysisSlide = loadable(
  () => import('@/components/HorizontallySlidingCompanyTile/AnalysisSlide'),
  {
    ssr: false,
  }
);

const ResearchSlide = loadable(
  () => import('@/components/HorizontallySlidingCompanyTile/ResearchSlide'),
  {
    ssr: false,
  }
);

const Pagination = loadable(() => import('@/components/Pagination'));

const GuestRegistrationModalOnScroll = loadable(
  () => import('@/components/GuestRegistrationModalOnScroll')
);

interface TileProps extends Company {
  index: number;
  pageType: PageType;
  isFetching: boolean;
  statementKeys: string[];
  preFetchCompany: (id: string) => void;
}

const MemoTile = memo((props: TileProps) => (
  <HorizontallySlidingCompanyTile
    index={props.index}
    key={`discover-tile-${props.companyId}`}
    name={props.shortName}
    image={props.mainThumb}
    canonicalUrl={props.canonicalUrl}
    companyId={props.companyId}
    uniqueSymbol={props.uniqueSymbol}
    tickerSymbol={props.tickerSymbol}
    description={props.shortDescription}
    snowflakePoints={props.snowflakePoints}
    onLongHover={props.preFetchCompany}
  >
    <StatementsSlide
      isFetching={props.isFetching}
      title={`${props.pageType} Criteria`}
      statements={props.statements}
      statementKeys={props.statementKeys}
      canonicalUrl={props.canonicalUrl}
    />
    <RisksRewardsSlide
      isFetching={props.isFetching}
      statements={props.statements}
      riskKeys={props.riskKeys}
      rewardKeys={props.rewardKeys}
      hasRisks={props.hasRisks}
      hasRewards={props.hasRewards}
      tickerSymbol={props.tickerSymbol}
      canonicalUrl={props.canonicalUrl}
    />
    <AnalysisSlide
      companyId={props.companyId}
      canonicalUrl={props.canonicalUrl}
    />
    <ResearchSlide
      companyName={props.shortName}
      canonicalUrl={props.canonicalUrl}
    />
  </HorizontallySlidingCompanyTile>
));

const PAGE_SIZE = 8;

const DiscoverPageV2 = () => {
  const theme = useTheme();
  const { isMobile, isTablet } = useUserAgent();
  const { trackEvent } = useTracking();
  const { data: user, isFetched: isUserFetched } = useUser();
  /**
   * Redux
   */
  const {
    id = '0',
    name = 'name',
    market = 'global',
    industry = 'any',
    pageQuery,
    canonicalUrl,
    navigateTo,
    getPaginatedRoute,
    onAdvancedFilters,
    preFetchCompany,
  } = useReduxStateForDiscover();
  //
  useEffect(() => {
    trackEvent({
      type: 'track',
      subject: 'page',
      action: 'landed',
      id,
      pageNumber: pageQuery,
    });
  }, [pageQuery]);
  //  Until this can be derived server side
  const pageType: PageType = getPageType(id as PageId);
  /**
   * Statements
   */
  const statementKeys = useMemo(() => getStatementKeys(pageType), [pageType]);
  /**
   * Rules
   */
  const {
    data: { rules, imageUrl },
  } = useGridView({ id });

  const updatedRules = useMemo(
    () =>
      typeof rules === 'undefined'
        ? undefined
        : updateRule(rules)(market, industry),
    [rules, market, industry]
  );

  /**
   * Grid query
   */
  const {
    data: grid,
    fetchStatus: gridCompaniesFetchStatus,
    isError: isGridError,
    isFetching: isFetchingGridCompanies,
    isFetched,
  } = useGridCompanies({
    rules: updatedRules,
    pageQuery: pageQuery - 1,
    pageSize: PAGE_SIZE,
    include: ['grid', 'info', 'score', 'statements'],
    statementsFilter: statementKeys,
  });
  /**
   * Get market options
   */
  const marketOptions = useMarketOptions(
    true,
    ROUTE_INVESTING_IDEA_DISCOVERY_PAGE,
    {
      id,
      name,
      industry,
    }
  );
  /**
   * Get industry options
   */
  const { industryOptions } = useIndustryOptionsByGridRule({
    rules,
    market,
    id,
    name,
  });
  /**
   * Handle dropdown changes
   */
  const handlerMarketChange = useCallback(
    (market: string) =>
      navigateTo({
        route: ROUTE_INVESTING_IDEA_DISCOVERY_PAGE,
        market: market as PrimaryCountryISOElseGlobal,
        industry,
      }),
    [industry, navigateTo]
  );
  const handleIndustryChange = useCallback(
    (industry: string) =>
      navigateTo({
        route: ROUTE_INVESTING_IDEA_DISCOVERY_PAGE,
        market,
        industry,
      }),
    [market, navigateTo]
  );
  /**
   * Meta
   */
  const {
    title,
    description,
    metaTitle,
    metaDescription,
    question,
    answer,
    demonymCountry,
    breadCrumbs,
  } = useMetaData(pageType, market, industry, grid.pages[0].companies);
  const totalRecords = grid.pages[0].meta.totalRecords;
  const totalPages = grid.pages[0].meta.totalPages;
  const companyCount = useMemo(() => {
    return `${totalRecords} ${pluralise(
      totalRecords,
      'company',
      'companies'
    )} meet this criteria in the ${demonymCountry} market`;
  }, [demonymCountry, totalRecords]);

  const LoadingState = useMemo(
    () =>
      Array.from({ length: PAGE_SIZE }, (_n, i) => (
        <HorizontallySlidingCompanyTile key={`loading-state-tile-${i}`}>
          <StatementsSlide title={`${pageType} Criteria`} />
          <RisksRewardsSlide />
          <AnalysisSlide />
        </HorizontallySlidingCompanyTile>
      )),
    [pageType]
  );
  /**
   * Handle grid fetch error
   */
  if (isGridError)
    return (
      <OneColumn>
        <Grid templateColumnsMd="1fr 1fr 1fr">
          <Grid columnMd="2/3">
            <Message>
              We're sorry, but an error occured. Please try again later.
            </Message>
          </Grid>
        </Grid>
      </OneColumn>
    );
  return (
    <OneColumn>
      <SEO
        metaTitle={metaTitle}
        metaDescription={metaDescription}
        question={question}
        answer={answer}
        page={pageQuery}
        active={true}
        canonicalUrl={canonicalUrl}
        companies={grid.pages[0].companies}
      />
      <MiniPriceChartDefinitions />
      <Wrapper>
        <Filter>
          <MarketIndustryFilter
            marketOptions={marketOptions}
            industryOptions={industryOptions}
            selectedMarket={market}
            selectedIndustry={industry}
            onMarketChange={handlerMarketChange}
            onIndustryChange={handleIndustryChange}
            onAdvancedFilterClick={() => onAdvancedFilters(updatedRules)}
            hideIndustryFilter={pageType === 'Gold'}
          />
        </Filter>
        <Header
          pageType={pageType}
          title={title}
          imageUrl={imageUrl}
          description={
            <>
              <p>{question}</p>
              <p>{description}</p>
            </>
          }
          breadCrumbs={breadCrumbs}
        />
        <Grid templateColumnsMd="1fr" templateRows="72px">
          <Grid alignContent="center">
            {isFetchingGridCompanies ? (
              <TotalRecords>
                <span>
                  <Bones maxWidth={280} />
                </span>
              </TotalRecords>
            ) : (
              <If match={totalRecords > 0}>
                <TotalRecords>{companyCount}</TotalRecords>
              </If>
            )}
          </Grid>
        </Grid>
      </Wrapper>
      <If match={isFetched && totalRecords === 0}>
        <Grid templateColumnsMd="1fr 1fr 1fr" data-cy-id="empty-state">
          <Grid columnMd="2/3">
            <Message>
              There are 0 companies in the {demonymCountry} market that meet our
              criteria.
              <br />
              <RouterLink
                to={{
                  type: ROUTE_INVESTING_IDEA_DISCOVERY_PAGE,
                  payload: {
                    market: 'global',
                    id,
                    name,
                    industry: industry === 'any' ? undefined : industry,
                  },
                }}
                onClick={() => {
                  trackEvent({
                    type: 'track',
                    subject: 'zero_results_link',
                    action: 'click',
                    id,
                  });
                }}
              >
                View Top Global {pageType} Stocks
              </RouterLink>
            </Message>
          </Grid>
        </Grid>
      </If>
      {gridCompaniesFetchStatus === 'fetching'
        ? LoadingState
        : grid.pages[0].companies.map((n, i) => (
            <MemoTile
              index={i}
              key={n.companyId}
              pageType={pageType}
              isFetching={false}
              preFetchCompany={preFetchCompany}
              statementKeys={statementKeys}
              {...n}
            />
          ))}
      <If match={totalRecords === 0 || pageQuery === totalPages}>
        <EndState market={market} country={demonymCountry} />
      </If>
      <If match={totalRecords !== 0}>
        <Pagination
          currentPage={pageQuery}
          totalPages={totalPages || 0}
          actionLink={(page) =>
            getPaginatedRoute(ROUTE_INVESTING_IDEA_DISCOVERY_PAGE, page)
          }
          loading={
            gridCompaniesFetchStatus === 'fetching' || isFetchingGridCompanies
          }
        />
      </If>
      <If
        match={
          isUserFetched &&
          user.loggedIn === false &&
          grid.pages[0].companies.length > 3
        }
      >
        <Media
          query={{ maxWidth: theme.mdMaxRaw }}
          defaultMatches={isMobile || isTablet}
        >
          {(matches) => (
            <GuestRegistrationModalOnScroll
              pixelLimit={matches ? 2500 : 1000}
            />
          )}
        </Media>
      </If>
    </OneColumn>
  );
};

export default track({ type: 'track', subject: 'page' })(
  DiscoverPageV2
) as typeof DiscoverPageV2;
